<template>
  <md-empty-state
    v-if="error != null">
    <font-awesome-icon class="md-empty-state-icon" :icon="['fad', 'face-sad-tear']" />
    <strong class="md-empty-state-label">An error has occurred</strong>
    <p class="md-empty-state-description">
      <span>{{ error }}</span><br>Please try again later
    </p>
    <md-button
      @click="goHome"
      class="md-raised">
      Go Hamilton Home Page
    </md-button>
  </md-empty-state>
  <md-empty-state
    v-else-if="dataLoaded && !productDetails.IsValidPart && !isOptionedSku">
    <font-awesome-icon class="md-empty-state-icon" :icon="['fad', 'face-sad-tear']" />
    <strong class="md-empty-state-label">Part Not Found</strong>
    <p class="md-empty-state-description">
      The part you are looking is not here. You can use the Product Finder to search all parts.
    </p>
    <md-button
      @click="goToProductFinder"
      class="md-raised">
      Go To Product Finder
    </md-button>
  </md-empty-state>
  <section
    v-else
    class="product-details-wrapper">
    <spinner class="product-details-spinner" :show-spinner="showSpinner" />
    <template
      v-if="productDetails">
      <contact-modal
        :cad-user.sync="cadUser"
        :display-modal.sync="displayCADModal"
        @registration-confirmed="onRegistrationConfirmed" />
      <section class="container product-header-wrapper">
        <product-header
          ref="productHeader"
          class="row"
          :cad-user="cadUser"
          :product-details="productDetails"
          :preloaded-option-combos="preloadedOptionCombos"
          :preloaded-option-types="preloadedOptionTypes"
          :options-load-complete="optionsLoadComplete"
          :parts-list="selectedPartsList"
          :is-valid-cad-user="isValidCadUser"
          :product-type="productDetails.ProductType"
          :selected-attributes="initialOptionAttrs"
          :search-attributes="selectedAttrs"
          :is-search-disabled="searchDisabled"
          :has-product-config-attributes="productDetails.HasProductConfigAttributes"
          @display-cad-modal="displayCADModal = $event"
          :part-id="partId"
          @part-id-updated="onPartIdUpdated"
        />
      </section>
      <div class="container">
        <div
          class="specifications"
          :class="{'has-charts': hasCharts}">
          <product-specifications
            v-if="productDetails.Specifications != null"
            :key="`specs-${productDetails.PartID || partId || 'base'}`"
            :has-product-config-attributes="productDetails.HasProductConfigAttributes"
            :product-type="productDetails.ProductType"
            :wheel-image-url="productDetails.Wheel_ImageURL"
            :product-specifications="productDetails.Specifications"
            :search-disabled.sync="searchDisabled"
            :selected-attrs.sync="selectedAttrs" />
          <wheel-characteristics
            v-if="productDetails.WheelCharacteristics != null"
            :key="`wheel-${productDetails.PartID || partId || 'base'}`"
            :show-wheel-type-image="productDetails.Show_WheelTypeImage"
            :wheel-characteristics="productDetails.WheelCharacteristics" />
          <product-charts
            v-if="hasCharts"
            :chart-data="productDetails.ErgoProfile" />
        </div>
      </div>
    </template>
  </section>
</template>

<script>
import { getDetailsAPI, getIsValidCADUser, getPartOptionCombos, getOptionTypes } from '../../api'
import Spinner from '../Utilities/Spinner'
import ProductHeader from './ProductHeader'
import ProductCharts from './ProductCharts'
import ProductSpecifications from './ProductSpecifications'
import WheelCharacteristics from './WheelCharacteristics'
import ContactModal from './ContactModal'
// Removed unused PartOptions components


// Helper to extract base partId from URL (ignores query params)
function getBasePartIdFromUrl() {
  const href = window.location.href;
  // Remove query string if present
  let partIdSection = href.split('PartID/')[1] || href.split('PartId/')[1] || '';
  if (partIdSection.includes('?')) {
    partIdSection = partIdSection.split('?')[0];
  }
  return partIdSection;
}

const urlParams = new URLSearchParams(window.location.search);
const partIdQueryParam = urlParams.get('PartId') || urlParams.get('PartID');
const partIdUrlParam = getBasePartIdFromUrl();

export default {
  name: 'product-details',
  components: {
    ContactModal,
    WheelCharacteristics,
    ProductSpecifications,
    ProductCharts,
    ProductHeader,
    Spinner
  },
  props: {
    detailLink: {
      type: String,
      required: false
    }
  },
  data () {
    return {
      // DNN Module services
      svc: {
        moduleId: this.$attrs['module-id'],
        path: 'AcuitiSolutions/CatalogDetail',
        controller: 'ItemController'
      },
      antiForgery: null,
      moduleService: {},
      // Component Items
      dataLoaded: false,
      displayCADModal: false,
      isMobile: true,
      productDetails: null,
      cadUser: {
        Address: null,
        CADUserID: 0,
        City: null,
        Company: null,
        DownloadFormats: null,
        Email: null,
        FullName: null,
        IsValidCADUser: false,
        Phone: null,
        State: null,
        Zipcode: null
      },
  partId: partIdUrlParam || partIdQueryParam, // always base partId, never with query params
      productTitle: '',
      searchDisabled: true,
      selectedAttrs: [],
      initialOptionAttrs: [],
      showSpinner: true,
      selectedPartsList: [],
      error: null,
      redirecting: false,
      isSilentUpdating: false,
      requestSequence: 0,
      latestAppliedRequest: 0,
      optionLoadSequence: 0,
      // Preload option data to avoid content shift
      preloadedOptionCombos: null,
      preloadedOptionTypes: [],
      lastLoadedPartId: null,
      optionsLoadComplete: false,
        optionsLoadPartId: ''
    }
  },
  
computed: {
  isValidCadUser () {
    return this.cadUser.IsValidCADUser
  },

  hasCharts () {
    return this.productDetails?.ErgoProfile !== undefined &&
           this.productDetails.ErgoProfile.length > 0
  },

  isOptionedSku () {
    if (!this.partId) return false

    const clean = String(this.partId).split('?')[0]
    const segments = clean.split('-')

    // Wheels have 4 base segments
    if (clean.startsWith('W-')) {
      return segments.length > 4
    }

    // Casters / others have 3 base segments
    return segments.length > 3
  }
},

  methods: {
    markOptionsLoadComplete(partId, optionLoadId = null) {
      if (optionLoadId && this.shouldAbortOptionLoad(optionLoadId)) return

      const normalizedPartId = String(partId || '').split('?')[0]
      if (!normalizedPartId || normalizedPartId !== this.optionsLoadPartId) return

      this.optionsLoadComplete = true
    },
    resolvePartRequest(partIdValue) {
      const cleanPartId = String(partIdValue || '').split('?')[0];
      const segments = cleanPartId.split('-');
      const isWheel = cleanPartId.startsWith('W-');
      const baseSegments = isWheel ? 4 : 3;
      const isOptionedSku = segments.length > baseSegments;
      const basePartId = segments.slice(0, baseSegments).join('-');
      const optionGroups = isOptionedSku
        ? parseOptionGroups(segments.slice(baseSegments))
        : [];

      return {
        cleanPartId,
        segments,
        isOptionedSku,
        basePartId,
        optionGroups
      };
    },
    onPartIdUpdated(newPartId) {
      if (!newPartId) return;

      const incoming = String(newPartId).split('?')[0];
      const current = String(this.partId || '').split('?')[0];
      const request = this.resolvePartRequest(incoming);
      const shouldForceBaseRefresh = !request.isOptionedSku && this.lastLoadedPartId !== incoming;
      const shouldRefreshAfterOptionClear = !request.isOptionedSku && Array.isArray(this.initialOptionAttrs) && this.initialOptionAttrs.length > 0;

      // ...removed TRACE log...

      if (incoming === current && !shouldForceBaseRefresh && !shouldRefreshAfterOptionClear) {
        return;
      }

      if (shouldRefreshAfterOptionClear) {
        this.initialOptionAttrs = [];
      }

      this.partId = incoming;
      this.getDetails({
        silent: this.dataLoaded,
        syncUrl: true
      });
    },
    applyProductDetailsPatch(res, { silent }) {
      if (!silent || !this.productDetails) {
        this.productDetails = res;
        return;
      }

      const target = this.productDetails;
      Object.keys(target).forEach(key => {
        if (!(key in res)) {
          this.$delete(target, key);
        }
      });

      Object.keys(res).forEach(key => {
        this.$set(target, key, res[key]);
      });
    },
    syncUrlFromSelection(request, res) {
      try {
        const url = new URL(window.location.href);
        const backendPartId = String(res?.PartID || '').split('?')[0];
        const fullPartId = backendPartId || request?.cleanPartId || String(this.partId || '').split('?')[0];

        // Keep canonical detail URL in path form: .../PartID/<full-part-id>
        if (fullPartId) {
          const path = url.pathname;
          const partIdMatch = path.match(/\/(PartID|PartId)\/([^?#]+)/);
          if (partIdMatch) {
            url.pathname = path.replace(partIdMatch[0], `/${partIdMatch[1]}/${fullPartId}`);
          }
        }

        // Remove query-based option routing for cleaner shareable URLs.
        url.searchParams.delete('options');
        url.searchParams.delete('PartId');
        url.searchParams.delete('PartID');

        window.history.replaceState({}, '', url.toString());
      } catch (e) {
        console.warn('[ProductDetails] syncUrlFromSelection failed', e);
      }
    },
    shouldAbortOptionLoad(optionLoadId) {
      if (!optionLoadId) return false
      return optionLoadId !== this.optionLoadSequence
    },
    // Ensure selectedAttrs reflects the current optioned SKU after combos are loaded.
    // Uses API-provided SelectedOptions if they exist; otherwise derives from the
    // part ID suffix (e.g. S-WH-4NYB-4SL-FITG-BRUSH → '4SL-FITG-BRUSH').
    seedSelectedAttrsFromPartId(request) {
      if (!request || !request.isOptionedSku) return
      // API already provided selections — nothing to do
      if (this.initialOptionAttrs && this.initialOptionAttrs.length > 0) return
      const suffix = request.cleanPartId.slice(request.basePartId.length + 1)
      if (suffix) {
        this.initialOptionAttrs = [suffix]
      }
    },
    async getDetails ({ silent = false, syncUrl = false } = {}) {
      const requestId = ++this.requestSequence;

      try {
        if (!silent) {
          this.showSpinner = true;
        } else {
          this.isSilentUpdating = true;
        }

        const request = this.resolvePartRequest(this.partId);
        this.optionsLoadPartId = request.cleanPartId
        this.optionsLoadComplete = false
        // Always use the full part ID (including options) for the API call
        const requestPartId = request.cleanPartId;
        const priorRequest = this.resolvePartRequest(this.lastLoadedPartId || this.partId);
        const basePartChanged = priorRequest.basePartId !== request.basePartId;
        const existingItems = Array.isArray(this.productDetails?.Items)
          ? this.productDetails.Items
          : [];
        const existingPartOptions = Array.isArray(this.productDetails?.PartOptions)
          ? this.productDetails.PartOptions
          : [];

        // ...removed TRACE log...

        const res = await getDetailsAPI(requestPartId, this.detailLink);

        // Ignore stale responses from older requests.
        if (requestId < this.latestAppliedRequest) {
          return;
        }
        this.latestAppliedRequest = requestId;

        // Prefer backend-supplied part options as the canonical items source.
        if (!Array.isArray(res.Items) || res.Items.length === 0) {
          res.Items = Array.isArray(res.PartOptions) ? res.PartOptions : [];
        }

        // Preserve option cards for same-base silent updates when API omits Items for optioned responses,
        // OR when PartOptions contains accessory-like rows (no OptionID/OptionType) that can't render as
        // option cards and would blank the grid.
        const hasRenderableOptionItems = (items) => Array.isArray(items) && items.some(item => (
          item && (item.OptionID != null || item.OptionType != null)
        ));
        if (silent && !basePartChanged && !hasRenderableOptionItems(res.Items)) {
          const preloadedFallback = (this.preloadedOptionCombos &&
            (this.preloadedOptionCombos.Items || this.preloadedOptionCombos.items)) || [];
          const source = existingItems.length > 0 ? existingItems : preloadedFallback;
          if (source.length > 0) {
            res.Items = [...source];
          }
        }

        // Keep PartOptions available as a fallback source for option-card rendering.
        if (silent && !basePartChanged && (!Array.isArray(res.PartOptions) || res.PartOptions.length === 0) && existingPartOptions.length > 0) {
          res.PartOptions = [...existingPartOptions];
        }

        this.applyProductDetailsPatch(res, { silent });
        this.lastLoadedPartId = this.partId;
        // -------------------------------------------
        // Apply backend-selected options.
        const selectedOptionsSource = Array.isArray(res.SelectedOptions)
          ? res.SelectedOptions
          : (typeof res.SelectedOptions === 'string' && res.SelectedOptions.trim()
              ? [res.SelectedOptions.trim()]
              : []);
        // SelectedOptions is authoritative from API and can arrive comma-delimited.
        const finalSelectedOptions = selectedOptionsSource
          .flatMap(option => String(option || '').split(','))
          .map(option => option.trim())
          .filter(Boolean);
        this.initialOptionAttrs = [...finalSelectedOptions];

        // Only fallback for invalid BASE parts
        if (!res.IsValidPart && !request.isOptionedSku) {
          this.handlePartIdFallback();
          return;
        }

        // Preload / re-seed options:
        //  - Always on initial (non-silent) load
        //  - Always when the base part changed
        //  - Always on a silent BASE-part request (e.g. deselect back to base) so that
        //    productDetails.Items is always sourced from the clean JSON combo endpoint,
        //    not from the potentially-different XML structure returned by getDetailsAPI.
        if (this.productDetails.HasProductConfigAttributes && (!silent || basePartChanged || !request.isOptionedSku)) {
          const optionLoadId = ++this.optionLoadSequence
          const loadTask = this.loadInitialOptions({ optionLoadId, partId: request.cleanPartId })
          if (silent) {
            await loadTask
            // After combos loaded on silent update, re-seed selection from API or part ID suffix
            this.seedSelectedAttrsFromPartId(request)
          } else {
            loadTask
              .then(() => {
                // Combos are now loaded. Seed selection if API didn't provide SelectedOptions.
                // This fires before PartOptionsNew mounts (mount is triggered by optionsLoadComplete).
                this.seedSelectedAttrsFromPartId(request)
              })
              .catch(e => {
                console.warn('[ProductDetails] background option preload failed', e)
              })
          }
        } else {
          this.markOptionsLoadComplete(request.cleanPartId)
        }

        if (!silent) {
          this.showSpinner = false;
        }

        if (syncUrl) {
          this.syncUrlFromSelection(request, res);
        }
        this.isSilentUpdating = false;
        this.dataLoaded = true;

      } catch (err) {
        this.error = err;
        this.isSilentUpdating = false;
        // Fallback logic trigger
        const request = this.resolvePartRequest(this.partId);
        if (!request.isOptionedSku) {
          this.handlePartIdFallback();
        }
      }
    },
    async loadInitialOptions({ optionLoadId = null, partId = '' } = {}) {
  try {
    if (this.shouldAbortOptionLoad(optionLoadId)) return;
    if (!this.productDetails || !this.productDetails.PartID) return;

    const existingItems = Array.isArray(this.productDetails.Items)
      ? this.productDetails.Items
      : [];
    const existingCombos = this.productDetails.OptionCombos || this.productDetails.optionCombos || null;
    const existingTypes = this.productDetails.OptionTypes || this.productDetails.optionTypes || [];
    const hasBackendOptionPayload = !!existingCombos && Array.isArray(existingTypes) && existingTypes.length > 0;

    if (hasBackendOptionPayload) {
      if (this.shouldAbortOptionLoad(optionLoadId)) return;
      this.preloadedOptionCombos = existingCombos;
      this.preloadedOptionTypes = existingTypes;
      const comboItems = (existingCombos && (existingCombos.Items || existingCombos.items)) || [];
      if (comboItems.length && !existingItems.length) {
        this.$set(this.productDetails, 'Items', comboItems);
      }
      this.markOptionsLoadComplete(partId, optionLoadId)
      return;
    }

    // If preloaded data is already available from a prior loadInitialOptions call,
    // re-apply it directly instead of hitting the network again. This ensures
    // productDetails.Items is always sourced from the clean JSON combo data even
    // after a silent base-part refresh (e.g. deselect back to base).
    const hasCachedPreload =
      this.preloadedOptionCombos &&
      Array.isArray(this.preloadedOptionTypes) &&
      this.preloadedOptionTypes.length > 0;

    if (hasCachedPreload) {
      if (this.shouldAbortOptionLoad(optionLoadId)) return;
      const comboItems =
        (this.preloadedOptionCombos && (this.preloadedOptionCombos.Items || this.preloadedOptionCombos.items)) || [];
      if (comboItems.length) {
        this.$set(this.productDetails, 'Items', comboItems);
      }
      this.markOptionsLoadComplete(partId, optionLoadId)
      return;
    }

    // --------------------------------------------------
    // FORCE base part resolution (temporary but correct)
    // --------------------------------------------------
    const fullPartId = String(this.productDetails.PartID).split('?')[0];
    const segments = fullPartId.split('-');

    let basePartId;
    if (fullPartId.startsWith('W-')) {
      // Wheels: W-XXXX-XXXX-XXXX
      basePartId = segments.slice(0, 4).join('-');
    } else {
      // Casters / others: S-WH-4NYB
      basePartId = segments.slice(0, 3).join('-');
    }

    const svc = this.moduleService[`svc-${this.moduleId}`];
    const antiForgeryList = document.getElementsByName('__RequestVerificationToken');
    const antiForgery = antiForgeryList && antiForgeryList[0]
      ? antiForgeryList[0].value
      : undefined;

    // --------------------------------------------------
    // ALWAYS load options from BASE PART ONLY
    // --------------------------------------------------
    const [combos, types] = await Promise.all([
      getPartOptionCombos(basePartId, svc, antiForgery).then(result => ({ result })),
      getOptionTypes(basePartId, svc, antiForgery).then(result => ({ result }))
    ]);

    const combosData = combos?.result;
    const typesData = types?.result;
  if (this.shouldAbortOptionLoad(optionLoadId)) return;
// Force reactivity (unchanged)
    this.preloadedOptionCombos = combosData || null;
    this.preloadedOptionTypes = Array.isArray(typesData) ? typesData : [];

    // Back-fill items if needed (legacy compatibility)
    const comboItems = (combosData && (combosData.Items || combosData.items)) || [];
    if (comboItems.length) {
      this.$set(this.productDetails, 'Items', comboItems);
    } else if (!existingItems.length && Array.isArray(this.productDetails.PartOptions)) {
      this.$set(this.productDetails, 'Items', this.productDetails.PartOptions);
    }
    this.markOptionsLoadComplete(partId, optionLoadId)
  } catch (e) {
    console.warn('[ProductDetails] loadInitialOptions failed', e);
    this.markOptionsLoadComplete(partId, optionLoadId)
  }
},
    validateUser () {
      getIsValidCADUser()
        .then(res => {
          if (res && res.IsValidCADUser) {
            this.cadUser = res
            // Persist email locally so cookie loss doesn't force re-entry
            try { localStorage.setItem('hc_cad_email', res.Email || '') } catch (e) {}
          } else {
            // Cookie unavailable — try locally stored email as fallback
            try {
              const savedEmail = localStorage.getItem('hc_cad_email')
              if (savedEmail) {
                this.cadUser.Email = savedEmail
                this.cadUser.IsValidCADUser = true
              }
            } catch (e) {}
          }
        })
    },
    goToProductFinder () {
      window.location.href = '/Configurator-Search-Results'
    },
    goHome () {
      window.location.href = '/'
    },
    

onRegistrationConfirmed () {
  // Persist email so the user isn't prompted again after page navigation
  try {
    if (this.cadUser.Email) {
      localStorage.setItem('hc_cad_email', this.cadUser.Email)
    }
  } catch (e) {}
},

    async handlePartIdFallback() {
  const cleanPartId = String(this.partId).split('?')[0];
  const segments = cleanPartId.split('-');

  let basePartId;

  if (cleanPartId.startsWith('W-')) {
    basePartId = segments.slice(0, 4).join('-');
  } else {
    basePartId = segments.slice(0, 3).join('-');
  }

  try {
    await getDetailsAPI(basePartId, this.detailLink);
  } catch (baseErr) {
    console.warn('[ProductDetails] handlePartIdFallback: error fetching base part:', baseErr);
  }
},

  },
  created () {
    this.moduleService[`svc-${this.moduleId}`] = this.svc;
    this.getDetails({
      silent: false,
      syncUrl: true
    });
  },
  mounted () {
    this.validateUser();
    // Auto-select options if present in query param
    const urlParams = new URLSearchParams(window.location.search);
    const optionsParam = urlParams.get('options');
    if (optionsParam) {
      // Split by | to get option groups
      const optionGroups = optionsParam.split('|');
      // Example: set initialOptionAttrs or other state as needed
      this.initialOptionAttrs = optionGroups;
    }
  },
}

// Helper to parse option groups from segments
function parseOptionGroups(optionSegments) {
  // Example logic: treat contiguous segments as a single group if they match known patterns
  // For now, join all segments as a single group (can be improved if needed)
  // e.g. ["4SL", "FITG", "ERZB", "BRUSH"] => ["4SL", "FITG-ERZB-BRUSH"] if needed
  // This can be customized based on business rules
  return [optionSegments.join('-')];
}
</script>

<style lang="scss">
  @import "../../assets/variables";
  .md-empty-state-label {
    margin-top: 2rem;
    font-size: 2.5rem;
  }
  .product-details-wrapper {
    

    .specifications {
      display: block;
      --columnWidth: 50%;
      --flex-grow: 0;
      &.has-charts {
        --columnWidth: 33%;
        --flex-grow: 1;
      }

      > div {
        > h2 {
          margin-bottom: 1rem;
        }
      }

      @media screen and (min-width: $large) {
        display: flex;
        flex-direction: row;
        margin: 2rem 0;
        flex-wrap: wrap;
        justify-content: center;
        --flex-grow: 0;

        > div {
          flex: var(--flex-grow) 1 var(--columnWidth);
          min-width: 32rem;
          padding: 1rem;
          @media screen and (max-width: $xx-large) {
            &.product-charts {
              flex: 2 2 var(--columnWidth);
              //min-width: 34rem;
            }
          }

        }
      }
      @media screen and (min-width: $x-large) {
        --columnWidth: 40%;
        &.has-charts {
          --columnWidth: 33%;
        }
      }
      @media screen and (min-width: $xx-large) {
        flex-wrap: nowrap;
      }
    }
  }

  .product-header-wrapper {
    margin-bottom: 1rem;
  }

  .hide-mobile {
    @media screen and (max-width: $medium) {
      display: none !important;
    }
  }
  .hide-desktop {
    @media screen and (min-width: $medium) {
      display: none !important;
    }
  }

  .part-options-section {
    &.container-fluid {
      @media screen and (min-width: $xxx-large) {
        padding: 0 1rem;
      }
    }
  }

  .product-details-spinner {
    min-height: 25rem;
  }

  .note-block {
    padding: .825rem;
    background: $lightGreyBg;
    color: $primaryColor;
    border: 1px solid lightGrey;
    border-left: 4px solid $warning;

    &.error {
      border-left: 4px solid $warning;
      color: $primaryColor;
    }
    &.success {
      border-left: 4px solid $success;
      color: $successText;
    }
  }

  a.md-button.md-theme-default,
  button.md-button.md-theme-default {
    &.md-outline {
      color: $primaryColor;
      border: 1px solid $primaryColor;
      &:hover {
        background-color: transparent;
        text-decoration: none;
      }
    }

    &.md-primary {
      background: $primaryColor;
      color: $white;
    }

    border: none;
    border-radius: none;
    display: inline-block;
    padding: 0.625rem;

    .md-button-content {
      font-size: 0.875rem;
      font-weight: 700;
      text-transform: none;
    }

    &:first-of-type {
      margin-left: 0;
    }
  }

  .md-theme-default {
    .breadcrumb li a {
      &:not(.md-button) {
        color: $white;
      }
    }
  }


</style>
